import { useIntl } from 'react-intl';
import { Header, HeaderProps } from '@rossum/components/header/Header';
import { Button, CircularProgress, Skeleton } from '@rossum/ui/material';
import { SettingsBreadcrumbs } from '../../../containers/Settings/SettingsBreadcrumbs';
import { Credential, CREDENTIAL_FORM_ID, credentialsPath } from '../core';

type CredentialFormHeaderProps = {
  /** Undefined while creating, and while the edited credential is loading. */
  credential?: Credential;
  isEditing?: boolean;
  isLoading?: boolean;
  isMutating?: boolean;
  onDelete?: () => void;
} & Pick<HeaderProps, 'scrollableDivRef'>;

export const CredentialFormHeader = ({
  credential,
  isEditing,
  isLoading,
  isMutating,
  onDelete,
  ...rest
}: CredentialFormHeaderProps) => {
  const intl = useIntl();

  const fallbackTitle = intl.formatMessage({
    id: isEditing
      ? 'features.credentials.form.header.title.fallback'
      : 'features.credentials.form.header.title.new',
  });

  // There is no form to submit until the edited credential is loaded.
  const hasForm = !isEditing || credential !== undefined;

  return (
    <Header
      {...rest}
      breadcrumbs={
        <SettingsBreadcrumbs
          breadcrumbs={[
            {
              label: intl.formatMessage({
                id: 'features.credentials.header.title',
              }),
              to: credentialsPath(),
            },
            { label: credential?.name ?? fallbackTitle },
          ]}
        />
      }
      title={
        isLoading
          ? () => <Skeleton variant="text" sx={{ width: 240 }} />
          : (credential?.name ?? fallbackTitle)
      }
      description={
        isLoading ? (
          <Skeleton variant="text" sx={{ width: 320 }} />
        ) : (
          (credential?.secret.baseApiUrl ?? '')
        )
      }
      buttons={[
        ...(onDelete
          ? [
              <Button
                key="delete"
                variant="outlined"
                color="secondary"
                data-cy="delete-credential"
                onClick={onDelete}
                disabled={isMutating}
              >
                {intl.formatMessage({
                  id: 'features.credentials.form.header.delete',
                })}
              </Button>,
            ]
          : []),
        <Button
          key="save"
          variant="contained"
          type="submit"
          data-cy="submit-credential-form"
          form={CREDENTIAL_FORM_ID}
          startIcon={
            isMutating && <CircularProgress color="inherit" size={20} />
          }
          disabled={isMutating || !hasForm}
        >
          {intl.formatMessage({
            id: 'features.credentials.form.header.save',
          })}
        </Button>,
      ]}
    />
  );
};
